import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { isInvoiceEnvelope, mergeInvoiceEnvelopes, invoicePeriod } from './billingWeekTracker';
import { loadBillingSignatureDocument } from '../db/company';

// Billing › Invoices: every invoice approval on the job, as DocuSign has it.
//
// The FTMS invoices go to VoltaGrid for approval in DocuSign — Anna sends most
// from the website, and Fusion picks them up (billingSignature.js
// pickUpWebsiteInvoices). A job can have invoices out with no billing summary
// built in Fusion at all (54026071), so this list reads the envelopes, not the
// summaries: one row per invoice, what it bills, who has approved it and who
// has it now, when it went out and when payment is due.
//
// What it bills, the total and the due date come off the Invoice Approval
// cover on page 1, which the API reads once per envelope and keeps as a
// 'cover' event (invoiceCover.coverFacts). Until it has, the type is read off
// the envelope's name and the due date and total show a dash.
//
// Pure helpers first (tested on their own), then the view.

const DEAD = ['voided', 'expired', 'failed'];
const lower = (v) => String(v || '').toLowerCase();
const utc = (v) => (v ? moment.utc(String(v).replace(' ', 'T')) : null);

// The cover's charge lines, by what the user asked to see: labor, equipment,
// third party (and the mobilization lump sum, which is none of the three).
const KIND_OF = {
    Labor: 'Labor', 'Per Diem': 'Labor',
    Equipment: 'Equipment', 'Equipment + fuel': 'Equipment', 'Fuel surcharge': 'Equipment',
    'Third party': 'Third party', 'Handling fee': 'Third party', 'Sales tax': 'Third party', Freight: 'Third party', 'Pass-through': 'Third party',
    Mobilization: 'Mobilization',
};
const KINDS = ['Labor', 'Equipment', 'Third party', 'Mobilization'];

/** The newest readable cover on an invoice's envelopes, or null. */
const coverOf = (parts) => {
    const read = parts.flatMap((p) => (p.events || []).filter((v) => v.Event === 'cover'))
        .sort((a, b) => String(b.Occurred_At_Utc || '').localeCompare(String(a.Occurred_At_Utc || '')));
    for (const v of read) {
        try {
            const facts = JSON.parse(v.Note || '');
            return facts && !facts.reason ? facts : null;
        } catch (e) { /* a note cut short reads as no cover */ }
    }
    return null;
};

/** What an invoice bills, from its cover's lines, else from its envelope's name. */
const kindsOf = (facts, e) => {
    const lines = facts?.charges || [];
    if (lines.length) {
        const seen = new Set(lines.map((c) => KIND_OF[c.area]).filter(Boolean));
        return KINDS.filter((k) => seen.has(k));
    }
    const name = `${e.Document_Name || ''} ${e.Subject || ''}`;
    if (/third\s*party/i.test(name)) return ['Third party'];
    if (/mobiliz/i.test(name)) return ['Mobilization'];
    return [];
};

/**
 * Each signer on an invoice's envelopes, in signing order: { name, signed, at,
 * declined, opened, now } — `now` is whoever it is waiting on (the lowest
 * routing order not yet signed, in each envelope still out). Those who have
 * signed come first, in the order they signed, so an invoice approved in two
 * envelopes reads in the order it was approved (F00294315: Ryan 10/21, then
 * Daniel 10/22, though Daniel's envelope is the older row).
 */
const signersOf = (parts) => parts.flatMap((p) => {
    const out = ['sent', 'delivered'].includes(lower(p.Status));
    const list = (p.recipients || []).slice()
        .sort((a, b) => (Number(a.Routing_Order) || 0) - (Number(b.Routing_Order) || 0));
    const people = list.length ? list.map((r) => ({
        name: String(r.Name || r.Email || '').trim(),
        order: Number(r.Routing_Order) || 1,
        signed: Boolean(r.Signed_At_Utc) || lower(r.Status) === 'completed',
        declined: Boolean(r.Declined_At_Utc) || lower(r.Status) === 'declined',
        opened: Boolean(r.Delivered_At_Utc) || lower(r.Status) === 'delivered',
        at: r.Signed_At_Utc || r.Declined_At_Utc || null,
    })) : String(p.Signer_Name || '').split(',').map((n) => n.trim()).filter(Boolean).map((name) => ({
        name, order: 1, signed: lower(p.Status) === 'completed', declined: false, opened: false, at: p.Completed_At_Utc || null,
    }));
    const next = Math.min(...people.filter((x) => !x.signed && !x.declined).map((x) => x.order));
    return people.map((x) => ({ ...x, now: out && !x.signed && !x.declined && x.order === next }));
}).map((x, i) => ({ x, i }))
    .sort((a, b) => (a.x.signed && b.x.signed ? String(a.x.at || '').localeCompare(String(b.x.at || '')) || a.i - b.i
        : a.x.signed ? -1 : b.x.signed ? 1 : a.i - b.i))
    .map(({ x }) => x);

/**
 * The PDF copies of an invoice in DocuSign, newest send first: one per
 * envelope that reached DocuSign (a re-send is a second copy). Each is what
 * billingSignatureDocument returns for that envelope: the invoice as it went
 * out and, once approved, with the signatures and DocuSign's certificate.
 */
const copiesOf = (parts) => parts.filter((p) => p.Envelope_ID).map((p) => ({
    rowId: Number(p.RowID),
    job: String(p.BU_ID || ''),
    envelopeId: String(p.Envelope_ID),
    name: String(p.Signed_Doc_Name || p.Document_Name || '').trim(),
    status: lower(p.Status),
    sentAt: p.Sent_At_Utc || null,
    sentBy: String(p.Sent_By || '').trim(),
})).sort((a, b) => String(b.sentAt || '').localeCompare(String(a.sentAt || '')) || b.rowId - a.rowId);

/**
 * The job's invoice approvals, newest first, those still out ahead of the
 * approved: [{ key, no, replaces, kinds, lines, from, to, sentAt, sentBy,
 * status, doneAt, signers, due, dueBy, net, total, amountDue, applied,
 * copies, doc }] — `copies` the PDFs in DocuSign (copiesOf), `doc` the
 * newest of them.
 * An invoice approved in several envelopes is one row (mergeInvoiceEnvelopes).
 * `voided` lists the invoices whose every approval was voided, less those an
 * invoice on the list replaces (F00294169, reissued as F00294315): that row
 * already says so.
 */
export const invoiceApprovals = (envelopes) => {
    const inv = (envelopes || []).filter(isInvoiceEnvelope);
    const live = mergeInvoiceEnvelopes(inv).filter((e) => !DEAD.includes(lower(e.Status)));
    const liveIds = new Set(live.map((e) => lower(e.Doc_ID)));
    const voided = [...new Set(inv.filter((e) => !liveIds.has(lower(e.Doc_ID)))
        .map((e) => invoicePeriod(e).no).filter(Boolean))];
    const rows = live.map((e) => {
        const parts = e.parts || [e];
        const facts = coverOf(parts);
        const named = invoicePeriod(e);
        const st = lower(e.Status);
        const no = facts?.no || named.no;
        const copies = copiesOf(parts);
        return {
            key: String(e.Doc_ID),
            no,
            copies,
            doc: copies[0] || null,
            // Reissued under a new number: row 65 is filed under F00294234
            // but its cover is F00294345.
            replaces: facts?.replaces || (facts?.no && facts.no !== named.no ? named.no : null),
            kinds: kindsOf(facts, e),
            lines: facts?.charges || [],
            from: facts ? facts.from : (/\d+\.\d+\.\d+\s*-/.test(String(e.Document_Name || '')) ? named.from : null),
            to: facts ? facts.to : (/\d+\.\d+\.\d+\s*-/.test(String(e.Document_Name || '')) ? named.to : null),
            sentAt: e.Sent_At_Utc || null,
            sentBy: [...new Set(parts.map((p) => p.Sent_By).filter(Boolean))].join(', '),
            status: st === 'completed' ? 'Approved' : st === 'declined' ? 'Declined' : st === 'created' ? 'Draft' : 'Out',
            doneAt: e.Completed_At_Utc || e.Declined_At_Utc || null,
            signers: signersOf(parts),
            due: facts?.due || null,
            dueBy: facts?.dueBy || null,
            net: facts?.net ?? null,
            total: facts?.total ?? null,
            amountDue: facts?.amountDue ?? null,
            applied: facts?.applied || null,
        };
    });
    const rank = (r) => (r.status === 'Out' || r.status === 'Draft' ? 0 : r.status === 'Declined' ? 1 : 2);
    rows.sort((a, b) => rank(a) - rank(b)
        || String(b.doneAt || b.sentAt || '').localeCompare(String(a.doneAt || a.sentAt || '')));
    const replaced = new Set(rows.map((r) => r.replaces).filter(Boolean));
    return { rows, voided: voided.filter((no) => !replaced.has(no)) };
};

/**
 * The invoice numbers the client has approved in DocuSign, upper case: each
 * approved row's number and the one it replaces (F00294315 approved settles
 * a summary still numbered F00294169). A summary on this list is invoiced,
 * whatever stage it was stored at.
 */
export const approvedNumbers = (rows) => new Set((rows || [])
    .filter((r) => r.status === 'Approved')
    .flatMap((r) => [r.no, r.replaces])
    .filter(Boolean)
    .map((no) => String(no).toUpperCase()));

/**
 * The table's total row: what the listed invoices bill, off their covers.
 * { total, due, approved, out, unread, declined } — `due` is after any
 * amount applied against another invoice, `out` what is still out for
 * approval (or a draft), `unread` the rows whose cover hasn't been read and
 * so carry no total yet. A declined invoice bills nothing until it is sent
 * again, so it is counted apart.
 */
export const invoiceTotals = (rows) => rows.reduce((s, r) => {
    if (r.status === 'Declined') return { ...s, declined: s.declined + 1 };
    if (r.total == null) return { ...s, unread: s.unread + 1 };
    const t = Number(r.total) || 0;
    const pending = r.status === 'Out' || r.status === 'Draft';
    return {
        total: s.total + t,
        due: s.due + (r.amountDue != null ? Number(r.amountDue) || 0 : t),
        approved: s.approved + (r.status === 'Approved' ? t : 0),
        out: s.out + (pending ? t : 0),
        unread: s.unread,
        declined: s.declined,
    };
}, { total: 0, due: 0, approved: 0, out: 0, unread: 0, declined: 0 });

// Where a due date came from (invoiceCover.coverFacts dueBy): the line under
// it, and what it says on hover.
const DUE_FROM = {
    invoice: { tail: '', title: 'As printed on the invoice' },
    invoiceTerms: { tail: ' from the invoice date', title: 'The invoice prints no due date: its date plus the payment terms' },
    cover: { tail: '', title: 'As printed on the cover' },
    terms: { tail: ' from the cover date', title: 'The cover prints no due date: its date plus the payment terms' },
};

// ── View ──

const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const md = (v) => { const m = utc(v); return m && m.isValid() ? m.local().format('M/D/YY') : ''; };
const ymdShort = (iso) => (iso ? moment(iso, 'YYYY-MM-DD').format('M/D/YY') : '');

const th = {
    fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)',
    borderBottom: '1px solid var(--border)', padding: '8px 12px', whiteSpace: 'nowrap',
};
const td = { fontSize: 14, padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--foreground)', verticalAlign: 'top' };
const sub = { fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2, whiteSpace: 'nowrap' };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const Pill = ({ tone, children, title }) => {
    const t = tone === 'good' ? { bg: 'var(--success-tint)', fg: 'var(--success-text)' }
        : tone === 'bad' ? { bg: 'var(--destructive-tint)', fg: 'var(--destructive-text)' }
            : tone === 'info' ? { bg: 'var(--primary-tint)', fg: 'var(--primary)' }
                : { bg: 'var(--muted)', fg: 'var(--text-secondary)', edge: 'var(--border)' };
    return (
        <span title={title} style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 7px', border: `1px solid ${t.edge || 'transparent'}`, borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 500, background: t.bg, color: t.fg, whiteSpace: 'nowrap', marginRight: 4, marginBottom: 2 }}>
            {children}
        </span>
    );
};

// Who has it: each signer in order, a tick and the date once signed, "has it"
// on whoever it is waiting on.
const Signers = ({ list }) => (
    <div style={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
        {list.map((x, i) => (
            <span key={`${x.name}-${i}`} style={{ fontSize: 13, whiteSpace: 'nowrap', color: x.signed || x.now ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                <span style={{ display: 'inline-block', width: 16, color: x.signed ? 'var(--success)' : x.declined ? 'var(--destructive)' : 'var(--muted-foreground)' }}>
                    {x.signed ? '✓' : x.declined ? '✕' : '○'}
                </span>
                {x.name}
                <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
                    {x.signed ? ` · ${md(x.at)}` : x.declined ? ` · declined ${md(x.at)}` : x.now ? (x.opened ? ' · has it, opened' : ' · has it') : ' · next'}
                </span>
            </span>
        ))}
    </div>
);

const STATUS_TONE = { Approved: 'good', Declined: 'bad', Out: 'info', Draft: 'neutral' };

// The PDF bytes billingSignatureDocument hands back, as a URL an iframe and a
// download link can take.
const pdfUrlOf = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

const btn = {
    font: 'inherit', fontSize: 14, fontWeight: 500, height: 32, padding: '0 12px', borderRadius: 'var(--radius)',
    display: 'inline-flex', alignItems: 'center', columnGap: 6, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap',
    background: 'var(--background)', color: 'var(--foreground)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xs)',
};
const btnPrimary = { ...btn, background: 'var(--primary)', color: 'var(--primary-foreground)', border: '1px solid transparent', boxShadow: 'none' };
const factLabel = { fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', marginBottom: 4 };
const COPY_STATE = { completed: 'approved', declined: 'declined', created: 'draft' };

// The charge lines off the invoice's cover, with the total and what is due.
const ChargeLines = ({ r }) => (
    r.lines.length ? (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
                {r.lines.map((c, i) => (
                    <tr key={`${c.area}-${i}`}>
                        <td style={{ fontSize: 13, padding: '2px 12px 2px 0', color: 'var(--text-secondary)' }}>{c.area}</td>
                        <td style={{ fontSize: 13, padding: '2px 0', ...num }}>{c.amount != null ? money(c.amount) : '—'}</td>
                    </tr>
                ))}
                {r.total != null ? (
                    <tr>
                        <td style={{ fontSize: 13, padding: '4px 12px 2px 0', fontWeight: 600, borderTop: '1px solid var(--border)' }}>Total</td>
                        <td style={{ fontSize: 13, padding: '4px 0 2px', fontWeight: 600, borderTop: '1px solid var(--border)', ...num }}>{money(r.total)}</td>
                    </tr>
                ) : null}
                {r.amountDue != null && r.amountDue !== r.total ? (
                    <tr>
                        <td style={{ fontSize: 13, padding: '2px 12px 2px 0', color: 'var(--text-secondary)' }}>{r.applied ? `Due, after applying against ${r.applied}` : 'Due'}</td>
                        <td style={{ fontSize: 13, padding: '2px 0', ...num }}>{money(r.amountDue)}</td>
                    </tr>
                ) : null}
            </tbody>
        </table>
    ) : (
        <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
            The cover has not been read yet — it is read the next time Billing opens.
        </span>
    )
);

/**
 * One invoice, opened from its row: the PDF as it sits in DocuSign beside
 * the facts off its cover. The PDF is read through billingSignatureDocument
 * (the envelope's combined document: the invoice as sent, and once approved
 * the signatures and certificate with it) and kept for the page's life, so a
 * second open is instant. `copies` lists every send; the newest shows first.
 */
const InvoicePanel = ({ r, consoleUrlBase, onClose, loadDocument, narrow }) => {
    const [copyId, setCopyId] = useState(r.doc?.rowId || 0);
    const [pdf, setPdf] = useState({ rowId: 0, url: '', name: '', error: '' });
    const copy = r.copies.find((c) => c.rowId === copyId) || r.doc;
    const copyRowId = copy?.rowId || 0;

    useEffect(() => {
        const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (!copy) return undefined;
        let stale = false;
        setPdf({ rowId: copy.rowId, url: '', name: '', error: '' });
        loadDocument(copy)
            .then((d) => { if (!stale) setPdf({ rowId: copy.rowId, url: d.url, name: d.name, error: '' }); })
            .catch((e) => { if (!stale) setPdf({ rowId: copy.rowId, url: '', name: '', error: e?.message || String(e) }); });
        return () => { stale = true; };
    }, [copyRowId]);

    const consoleUrl = copy ? `${consoleUrlBase || 'https://app.docusign.com/documents/details/'}${encodeURIComponent(copy.envelopeId)}` : '';
    const period = r.from && r.to ? `${ymdShort(r.from)} – ${ymdShort(r.to)}` : '';
    const stop = (ev) => ev.stopPropagation();
    const isCopy = (c) => c.rowId === copyRowId;
    return (
        <div role="presentation" onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: 'rgb(0 0 0 / 0.5)' }}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Invoice ${r.no || ''}`}
                data-testid="invoice-panel"
                onClick={stop}
                style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0, width: narrow ? '100vw' : 'min(1180px, 94vw)',
                    background: 'var(--popover)', color: 'var(--popover-foreground)', borderLeft: '1px solid var(--border)', boxShadow: 'var(--shadow-md)',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', columnGap: 12, padding: narrow ? '12px 16px' : '12px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', rowGap: 8 }}>
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', columnGap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 17, fontWeight: 600, borderLeft: '3px solid var(--destructive)', paddingLeft: 10, lineHeight: 1.15 }}>{`Invoice ${r.no || '—'}`}</span>
                            <Pill tone={STATUS_TONE[r.status]}>{r.status === 'Out' ? 'Out for approval' : r.status}</Pill>
                            {r.replaces ? <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{`replaces ${r.replaces}`}</span> : null}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, paddingLeft: 13 }}>
                            {[period, r.total != null ? money(r.total) : '', r.sentAt ? `sent ${md(r.sentAt)}${r.sentBy ? ` by ${r.sentBy}` : ''}` : ''].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                    {pdf.url ? <a href={pdf.url} download={pdf.name || `Invoice ${r.no || ''}.pdf`} style={btnPrimary}>Download PDF</a> : null}
                    {consoleUrl ? <a href={consoleUrl} target="_blank" rel="noopener noreferrer" style={btn}>Open in DocuSign</a> : null}
                    <button type="button" onClick={onClose} aria-label="Close" title="Close (Esc)" style={{ ...btn, width: 32, padding: 0, justifyContent: 'center', fontSize: 18 }}>×</button>
                </div>
                <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', flexGrow: 1, minHeight: 0 }}>
                    <div style={{ width: narrow ? 'auto' : 300, flexShrink: 0, overflowY: 'auto', padding: 16, borderRight: narrow ? 'none' : '1px solid var(--border)', borderBottom: narrow ? '1px solid var(--border)' : 'none', maxHeight: narrow ? '40vh' : 'none' }}>
                        <div style={factLabel}>Charges, off the cover</div>
                        <ChargeLines r={r} />
                        <div style={{ ...factLabel, marginTop: 16 }}>Approvals</div>
                        <Signers list={r.signers} />
                        {r.due ? (
                            <>
                                <div style={{ ...factLabel, marginTop: 16 }}>Payment due</div>
                                <div style={{ fontSize: 13 }}>
                                    {ymdShort(r.due)}
                                    {r.net != null ? <span style={{ color: 'var(--muted-foreground)' }}>{` · Net ${r.net}${(DUE_FROM[r.dueBy] || DUE_FROM.cover).tail}`}</span> : null}
                                </div>
                            </>
                        ) : null}
                        {r.copies.length > 1 ? (
                            <>
                                <div style={{ ...factLabel, marginTop: 16 }}>Copies in DocuSign</div>
                                <div style={{ display: 'flex', flexDirection: 'column', rowGap: 4 }}>
                                    {r.copies.map((c) => (
                                        <button
                                            key={c.rowId}
                                            type="button"
                                            aria-pressed={isCopy(c)}
                                            onClick={() => setCopyId(c.rowId)}
                                            style={{
                                                ...btn, height: 'auto', padding: '6px 10px', textAlign: 'left', whiteSpace: 'normal', boxShadow: 'none',
                                                background: isCopy(c) ? 'var(--muted)' : 'transparent', border: `1px solid ${isCopy(c) ? 'var(--border)' : 'transparent'}`,
                                            }}
                                        >
                                            <span style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: 13 }}>{`${md(c.sentAt) || 'not sent'}${c.sentBy ? ` · ${c.sentBy}` : ''}`}</span>
                                                <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{COPY_STATE[c.status] || 'out for approval'}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : null}
                        {copy?.name ? <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 16, wordBreak: 'break-word' }}>{copy.name}</div> : null}
                    </div>
                    <div style={{ flexGrow: 1, minWidth: 0, minHeight: 0, background: 'var(--subtle)', display: 'flex', alignItems: pdf.url ? 'stretch' : 'center', justifyContent: 'center' }}>
                        {!copy ? (
                            <span style={{ fontSize: 13, color: 'var(--muted-foreground)', padding: 24, textAlign: 'center' }}>This invoice never reached DocuSign, so there is no PDF to show.</span>
                        ) : pdf.url ? (
                            <iframe src={pdf.url} title={`Invoice ${r.no || ''} PDF`} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
                        ) : pdf.error ? (
                            <span style={{ fontSize: 13, color: 'var(--destructive-text)', padding: 24, textAlign: 'center' }}>{`The PDF could not be read from DocuSign — ${pdf.error}`}</span>
                        ) : (
                            <span style={{ fontSize: 13, color: 'var(--muted-foreground)', padding: 24 }}>Reading the PDF from DocuSign…</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const BillingInvoiceApprovals = ({ envelopes, loading, consoleUrlBase = '', loadDocument }) => {
    const [open, setOpen] = useState(null);
    // The PDFs already read, by envelope row: { url, name }. Object URLs are
    // revoked when the list unmounts.
    const cache = useRef(new Map());
    useEffect(() => () => { cache.current.forEach((d) => URL.revokeObjectURL(d.url)); cache.current.clear(); }, []);
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 800);
    useEffect(() => {
        const onResize = () => setNarrow(window.innerWidth < 800);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const readDocument = useCallback(async (copy) => {
        const had = cache.current.get(copy.rowId);
        if (had) return had;
        const res = await (loadDocument || loadBillingSignatureDocument)({ job: copy.job, rowId: copy.rowId });
        if (!res || res.error || !res.contentBase64) throw new Error(res?.error || 'no document came back');
        const d = { url: pdfUrlOf(res.contentBase64), name: res.name || copy.name || '' };
        cache.current.set(copy.rowId, d);
        return d;
    }, [loadDocument]);
    const close = useCallback(() => setOpen(null), []);

    const { rows, voided } = invoiceApprovals(envelopes);
    if (!rows.length) {
        return loading ? <div style={{ fontSize: 13, color: 'var(--muted-foreground)', padding: '4px 0 12px' }}>Loading invoice approvals…</div> : null;
    }
    const today = moment().format('YYYY-MM-DD');
    const sum = invoiceTotals(rows);
    const opened = open ? rows.find((r) => r.key === open) : null;
    const hoverOn = (ev) => { if (ev.currentTarget.getAttribute('aria-selected') !== 'true') ev.currentTarget.style.background = 'color-mix(in srgb, var(--muted) 60%, transparent)'; };
    const hoverOff = (ev) => { if (ev.currentTarget.getAttribute('aria-selected') !== 'true') ev.currentTarget.style.background = ''; };
    return (
        <div data-testid="invoice-approvals" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', columnGap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>In approval and approved</span>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>from DocuSign · click an invoice to see the PDF that went out · type and total read off each invoice&apos;s cover page, the due date off the invoice</span>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={th}>Invoice</th>
                            <th style={th}>Type</th>
                            <th style={th}>Sent</th>
                            <th style={th}>Approvals</th>
                            <th style={th}>Payment due</th>
                            <th style={{ ...th, textAlign: 'right' }}>Total</th>
                            <th style={th}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const isOpen = open === r.key;
                            const late = r.due && r.due < today && r.status !== 'Approved';
                            return (
                                <tr
                                    key={r.key}
                                    aria-selected={isOpen}
                                    onClick={() => setOpen(r.key)}
                                    onMouseEnter={hoverOn}
                                    onMouseLeave={hoverOff}
                                    title="Open the invoice and its PDF"
                                    style={{ cursor: 'pointer', background: isOpen ? 'var(--muted)' : undefined, boxShadow: isOpen ? 'inset 3px 0 0 var(--destructive)' : undefined }}
                                >
                                    <td style={td}>
                                        {/* The row opens on a click anywhere; this button is
                                            its keyboard stop (and carries the focus ring). */}
                                        <button
                                            type="button"
                                            aria-haspopup="dialog"
                                            aria-expanded={isOpen}
                                            onClick={(ev) => { ev.stopPropagation(); setOpen(r.key); }}
                                            style={{
                                                font: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--primary)', whiteSpace: 'nowrap',
                                                background: 'transparent', border: '1px solid transparent', boxShadow: 'none', padding: 0, cursor: 'pointer',
                                            }}
                                        >
                                            {r.no || '—'}
                                        </button>
                                        {r.from && r.to ? <div style={sub}>{`${ymdShort(r.from)} – ${ymdShort(r.to)}`}</div> : null}
                                        {r.replaces ? <div style={sub}>{`replaces ${r.replaces}`}</div> : null}
                                    </td>
                                    <td style={td}>
                                        {r.kinds.length ? r.kinds.map((k) => <Pill key={k}>{k}</Pill>) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                                    </td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        {md(r.sentAt) || '—'}
                                        {r.sentBy ? <div style={sub}>by {r.sentBy}</div> : null}
                                    </td>
                                    <td style={td}><Signers list={r.signers} /></td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        {r.due ? (
                                            <span style={{ color: late ? 'var(--destructive)' : undefined, fontVariantNumeric: 'tabular-nums' }}>{ymdShort(r.due)}</span>
                                        ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                                        {r.net != null ? (
                                            <div style={sub} title={(DUE_FROM[r.dueBy] || DUE_FROM.cover).title}>
                                                {`Net ${r.net}${(DUE_FROM[r.dueBy] || DUE_FROM.cover).tail}`}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td style={{ ...td, ...num, fontWeight: 600 }}>
                                        {r.total != null ? money(r.total) : <span style={{ color: 'var(--muted-foreground)', fontWeight: 400 }}>—</span>}
                                        {r.amountDue != null && r.amountDue !== r.total ? (
                                            <div style={{ ...sub, fontWeight: 400 }} title={r.applied ? `Applied against ${r.applied}` : undefined}>{`${money(r.amountDue)} due`}</div>
                                        ) : null}
                                    </td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        <Pill tone={STATUS_TONE[r.status]}>{r.status === 'Out' ? 'Out for approval' : r.status}</Pill>
                                        {r.status !== 'Out' && r.doneAt ? <div style={sub}>{md(r.doneAt)}</div> : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr data-testid="invoice-approvals-total">
                            <td colSpan={5} style={{ ...td, borderBottom: 'none', background: 'var(--subtle)', fontWeight: 600 }}>
                                {`Total, ${rows.length} invoice${rows.length === 1 ? '' : 's'}`}
                                {sum.unread ? <div style={{ ...sub, fontWeight: 400 }}>{`${sum.unread} with no cover read yet, not in the total`}</div> : null}
                                {sum.declined ? <div style={{ ...sub, fontWeight: 400 }}>{`${sum.declined} declined, not in the total`}</div> : null}
                            </td>
                            <td style={{ ...td, ...num, borderBottom: 'none', background: 'var(--subtle)', fontWeight: 600 }}>
                                {money(sum.total)}
                                {sum.due !== sum.total ? <div style={{ ...sub, fontWeight: 400 }}>{`${money(sum.due)} due`}</div> : null}
                            </td>
                            <td style={{ ...td, borderBottom: 'none', background: 'var(--subtle)' }}>
                                {sum.out ? <div style={{ ...sub, marginTop: 0 }}>{`${money(sum.approved)} approved`}</div> : null}
                                {sum.out ? <div style={sub}>{`${money(sum.out)} out`}</div> : null}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            {voided.length ? (
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 6 }}>
                    {`Voided in DocuSign and not shown: ${voided.join(', ')}`}
                </div>
            ) : null}
            {opened ? (
                <InvoicePanel key={opened.key} r={opened} consoleUrlBase={consoleUrlBase} onClose={close} loadDocument={readDocument} narrow={narrow} />
            ) : null}
        </div>
    );
};

export default BillingInvoiceApprovals;
